import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Layers, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const STATE = { waiting: "Waiting", running: "Working", done: "Done", failed: "Failed", skipped: "Skipped" };

const Row = ({ k, i, live }) => (
  <li data-testid={`light-fill-kit-${i}`} className="flex items-center gap-3 border-t border-stone-100 py-1.5 text-sm">
    <span className="min-w-0 flex-1 truncate text-[#2B2A26]">{k.destination} <span className="text-xs text-stone-400">· {k.collection === "founders" ? "Founder\u2019s" : "Researched"} · {k.edition}</span></span>
    <span data-testid={`light-fill-kit-status-${i}`} className="text-xs text-stone-500">
      {k.state === "running" && <Loader2 size={11} className="mr-1 inline animate-spin" />}{k.state === "waiting" && !live ? "Not reached" : STATE[k.state]}{k.note ? ` · ${k.note}` : ""}
    </span>
  </li>
);

export const LightFill = () => {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/light-fill").then((x) => setRun(x.data.run)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = run?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [running, load]);
  const post = async (url, msg) => {
    setBusy(true);
    try { const r = await http.post(url); toast.success(msg(r.data)); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const kits = run?.kits || [];
  const filled = kits.reduce((n, k) => n + (k.filled || 0), 0);
  return (
    <section data-testid="light-fill-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Layers size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Fill light pages</b>
        <span data-testid="light-fill-summary" className="text-sm text-stone-500">
          {running ? `Working kit by kit · ${filled} page(s) filled so far` : run ? `Last run ${run.status} · ${filled} page(s) filled` : "Adds web-verified detail to every page under 85% full, on every kit, and saves it straight in (uses AI credits)"}
        </span>
        <div className="ml-auto flex gap-2">
          {running ? (
            <button data-testid="light-fill-stop-btn" disabled={busy || run.stop} onClick={() => post("/light-fill/stop", () => "Stopping after the current kit")} className="rounded-sm border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-50">{run.stop ? "Stopping…" : "Stop"}</button>
          ) : (
            <button data-testid="light-fill-start-btn" disabled={busy} onClick={() => post("/light-fill", (x) => `Filling light pages on ${x.kits} kit(s)`)} className="inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">
              {busy && <Loader2 size={12} className="animate-spin" />} Fill all light pages
            </button>
          )}
        </div>
      </div>
      {kits.length > 0 && <ul className="mt-2 max-h-56 overflow-y-auto">{kits.map((k, i) => <Row key={k.id} k={k} i={i} live={running} />)}</ul>}
    </section>
  );
};
